import {
  type GetApiAdminCompanyByCompanyTreeSettingsIdResponse,
  usePatchAdminCompany,
} from '@sit/client-shared';
import type { TenantType } from '@sit/core';
import { useForm } from '@tanstack/react-form';
import { noop } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import Form from 'carbon-react/lib/components/form';
import Loader from 'carbon-react/lib/components/loader';
import { Option, Select } from 'carbon-react/lib/components/select';
import Textbox from 'carbon-react/lib/components/textbox';
import toastr from '../../../helpers/toastr';

export interface CompanyDetailsProps {
  company: GetApiAdminCompanyByCompanyTreeSettingsIdResponse;
}

const emptyToNull = <T extends string | null | undefined>(value: T) => {
  return value === '' || value == null ? null : value;
};

export function CompanyDetailsForm({ company }: CompanyDetailsProps) {
  const mutation = usePatchAdminCompany();

  const form = useForm({
    defaultValues: {
      externalId: company.externalId,
      tenantType: company.tenantType || 'customer_production',
      syncError: company.syncError || null,
    },
    onSubmit: async ({ value }) => {
      await mutation.mutateAsync(
        {
          body: {
            tenantType: value.tenantType,
            syncError: emptyToNull(value.syncError),
          },
          pathParams: {
            id: `${company.id}`,
          },
        },
        {
          onSuccess: () => {
            toastr.success('Company updated');
          },
          onError: (error) => {
            toastr.error(error.message);
          },
        },
      );
    },
  });

  return (
    <Form
      errorCount={form.state.errors.length}
      saveButton={
        <Button
          buttonType="primary"
          onClick={() => {
            void form.handleSubmit();
          }}
          disabled={form.state.isSubmitting}
        >
          {form.state.isSubmitting ? (
            <Loader isActive isInsideButton />
          ) : (
            'Save'
          )}
        </Button>
      }
    >
      <Textbox
        value={company.externalId}
        label="Company ID"
        readOnly
        onChange={noop}
      />
      <Textbox label="SIT ID" value={company.id} readOnly onChange={noop} />
      <Textbox
        label="Admin username"
        value={company.adminUsername ?? ''}
        readOnly
        onChange={noop}
      />
      <Textbox
        label="Admin email"
        value={company.adminEmail ?? ''}
        readOnly
        onChange={noop}
      />
      <Textbox
        label="Last login attempt"
        value={company.lastLoginAttempt ?? ''}
        readOnly
        onChange={noop}
      />
      <Textbox
        label="Sync start"
        value={company.syncStart ?? ''}
        readOnly
        onChange={noop}
      />
      <Textbox
        label="Sync end"
        value={company.syncEnd ?? ''}
        readOnly
        onChange={noop}
      />
      <Box display="flex">
        <Box flexGrow={1}>
          <form.Field
            name="syncError"
            validators={{
              onChange: ({ value }) => {
                return value === '' ? undefined : undefined;
              },
            }}
          >
            {(field) => (
              <Textbox
                name={field.name}
                value={field.state.value ?? ''}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                label="Sync error"
                readOnly
              />
            )}
          </form.Field>
        </Box>
        <Box mt="25px">
          <Button
            onClick={() => {
              form.setFieldValue('syncError', '');
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>
      <form.Field
        name="tenantType"
        validators={{
          onChange: ({ value }) => {
            return !value ? 'This field is required' : undefined;
          },
        }}
      >
        {(field) => (
          <Select
            name={field.name}
            value={field.state.value}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.target.value as TenantType)}
            label="Tenant type"
            required
          >
            <Option value="customer_production" text="Customer Production" />
            <Option value="partner_production" text="Partner Production" />
            <Option value="SIAP_production" text="SIAP Production" />
            <Option value="MEDC_production" text="MEDC Production" />
            <Option value="sandbox" text="Sandbox" />
            <Option value="developer" text="Developer" />
            <Option value="sales_demo" text="Sales Demo" />
            <Option value="implementation" text="Implementation" />
            <Option value="debug" text="Debug" />
            <Option value="test" text="Test" />
            <Option value="preview" text="Preview" />
            <Option value="other" text="Other" />
          </Select>
        )}
      </form.Field>
    </Form>
  );
}
